import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errMsg, api } from "../lib/api";
import { LangSwitch, useLang } from "../lib/i18n";
import { Modal } from "../components/shared";
import { GoogleButton } from "../components/GoogleAuth";

function ForgotModal({ initial, onClose }) {
  const { t } = useLang();
  const [email, setEmail] = useState(initial || "");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Modal title={t("login.forgot")} onClose={onClose} testid="forgot-modal">
      {sent ? <p className="text-sm" data-testid="forgot-sent">{t("login.forgotSent")}</p> : (
        <form onSubmit={send} className="space-y-3">
          <p className="text-sm text-slate-600">{t("login.forgotHelp")}</p>
          <input className="tl-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button className="tl-btn tl-btn-primary w-full" disabled={busy} data-testid="forgot-submit">{busy && <Loader2 className="animate-spin" size={16} />} {t("login.forgotSend")}</button>
        </form>
      )}
    </Modal>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState(localStorage.getItem("tl_saved_email") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(!!localStorage.getItem("tl_saved_email"));
  const [forgot, setForgot] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password, remember);
      localStorage.removeItem("tl_email");
      if (remember) localStorage.setItem("tl_saved_email", email); else localStorage.removeItem("tl_saved_email");
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="tl-stripe text-white p-8 lg:p-14 flex flex-col justify-between">
        <div className="tl-display text-amber-400 text-sm uppercase tracking-[0.3em] font-bold" data-testid="login-kicker">{t("login.kicker")}</div>
        <div className="tl-fade">
          <h1 className="text-5xl lg:text-7xl font-extrabold uppercase leading-[0.9]">TourLink</h1>
          <p className="mt-4 text-lg text-slate-300 max-w-md">{t("login.tagline")}</p>
        </div>
        <p className="text-xs text-slate-400 hidden lg:block" data-testid="login-access-note">{t("login.access")}</p>
      </div>
      <form onSubmit={submit} className="p-6 sm:p-12 flex flex-col justify-center max-w-md w-full mx-auto gap-5" data-testid="login-form">
        <div className="flex items-center justify-between"><h2 className="text-3xl font-bold uppercase">{t("login.title")}</h2><LangSwitch dark /></div>
        <label>
          <span className="tl-label">{t("login.email")}</span>
          <input className="tl-input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
        </label>
        <label>
          <span className="tl-label">{t("login.password")}</span>
          <input className="tl-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="h-4 w-4 accent-amber-600" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember-checkbox" />
          {t("login.remember")}
        </label>
        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">{error}</div>}
        <button className="tl-btn tl-btn-primary w-full text-lg" disabled={busy} data-testid="login-submit-button">
          {busy && <Loader2 className="animate-spin" size={18} />} {t("login.submit")}
        </button>
        <div className="flex items-center gap-2 text-xs text-slate-400"><span className="flex-1 h-px bg-slate-200" />ou<span className="flex-1 h-px bg-slate-200" /></div>
        <GoogleButton />
        <p className="text-xs text-slate-500" data-testid="google-login-note">Pas encore de compte ? Inscrivez-vous avec Google ou par e-mail et remplissez votre fiche : l'accès et le rôle sont attribués par l'administrateur ou un gestionnaire.</p>
        <Link to="/inscription" className="tl-btn tl-btn-ghost w-full border border-slate-300" data-testid="signup-link">Créer un compte avec mon e-mail</Link>
        <p className="text-xs text-slate-500">{t("login.help")}</p>
        <button type="button" className="text-sm font-semibold text-amber-700 underline text-left" onClick={() => setForgot(true)} data-testid="forgot-password-link">{t("login.forgot")}</button>
      </form>
      {forgot && <ForgotModal initial={email} onClose={() => setForgot(false)} />}
    </div>
  );
}
